/**
 * Caliber's Stage.seek skips updating video.currentTime while isPlaying && same
 * segment URL — transport actions only move the ticker. These helpers apply a
 * real seek against the loaded playerMap entry (or identify the media segment
 * for a force-reload fallback).
 *
 * Mock archive spans the full lookback with one short sample MP4; seek offsets
 * wrap with modulo so the same file repeats across the entire timescale.
 */

import {
  SAMPLE_PLAYBACK_DURATION_SECONDS,
  SAMPLE_PLAYBACK_SHORT_DURATION_SECONDS,
} from './sample-playback-media';

export interface LibrarySeekSegment {
  start: Date;
  end: Date;
  url: string;
  cameraId?: string;
}

export interface LibrarySeekCamera {
  id?: string | number;
  segments?: Record<string, Array<{ start: Date | string; end: Date | string; url?: string; cameraId?: string }>>;
}

export interface LibrarySeekPlayerMapEntry {
  currentUrl: string;
  segmentUrl: string;
  loadedStartTime: Date;
}

export function resolveMockMediaDurationSeconds(url: string | undefined | null): number {
  if (url?.includes('sample_video_12s')) {
    return SAMPLE_PLAYBACK_SHORT_DURATION_SECONDS;
  }
  return SAMPLE_PLAYBACK_DURATION_SECONDS;
}

/**
 * Map wall-clock time into a virtual tile the length of the sample media so
 * Caliber's `(time - segment.start)` seek lands inside the short MP4.
 */
export function toLoopingSeekSegment(
  segment: LibrarySeekSegment,
  time: Date,
  mediaDurationSeconds = resolveMockMediaDurationSeconds(segment.url),
): LibrarySeekSegment {
  const durationMs = Math.max(1, mediaDurationSeconds * 1000);
  const elapsedMs = time.getTime() - segment.start.getTime();
  const offsetMs = ((elapsedMs % durationMs) + durationMs) % durationMs;
  const virtualStart = new Date(time.getTime() - offsetMs);
  return {
    ...segment,
    start: virtualStart,
    end: new Date(virtualStart.getTime() + durationMs),
  };
}

/** Prefer continuous `archive` media for seeks; fall back to AI types. */
export function resolveMediaSegmentAtTime(
  camera: LibrarySeekCamera,
  motionTypes: readonly string[],
  time: Date,
): LibrarySeekSegment | null {
  const currentMs = time.getTime();
  if (!Number.isFinite(currentMs)) {
    return null;
  }

  const orderedTypes = [
    ...(camera.segments?.['archive']?.length ? (['archive'] as const) : []),
    // Legacy mocks that still put continuous media under `motion`.
    ...(camera.segments?.['motion']?.length && !camera.segments?.['archive']?.length
      ? (['motion'] as const)
      : []),
    ...motionTypes.filter((type) => type !== 'archive'),
  ];
  const seen = new Set<string>();

  for (const type of orderedTypes) {
    if (seen.has(type)) {
      continue;
    }
    seen.add(type);
    for (const seg of camera.segments?.[type] ?? []) {
      const start = new Date(seg.start);
      const end = new Date(seg.end);
      if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
        continue;
      }
      if (currentMs < start.getTime() || currentMs > end.getTime()) {
        continue;
      }
      const url = seg.url?.trim();
      if (!url) {
        continue;
      }
      const matched: LibrarySeekSegment = { start, end, url, cameraId: seg.cameraId };
      const mediaMs = resolveMockMediaDurationSeconds(url) * 1000;
      // Long mock archive → virtual loop tile so stage.seek stays in-file.
      if (type === 'archive' && end.getTime() - start.getTime() > mediaMs + 1) {
        return toLoopingSeekSegment(matched, time);
      }
      return matched;
    }
  }
  return null;
}

/**
 * Soft-seek within the currently loaded source while playing.
 * Returns false when the caller should fall back to a force-reload seek.
 */
export function trySoftSeekPlayingVideo(
  video: HTMLVideoElement,
  entry: LibrarySeekPlayerMapEntry | undefined,
  segment: LibrarySeekSegment,
  time: Date,
): boolean {
  if (!entry || entry.segmentUrl !== segment.url) {
    return false;
  }

  const loadedStartMs = entry.loadedStartTime.getTime();
  const targetMs = time.getTime();
  if (!Number.isFinite(loadedStartMs) || !Number.isFinite(targetMs)) {
    return false;
  }

  const mediaDuration =
    Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : resolveMockMediaDurationSeconds(segment.url);

  // Prefer offset against the (possibly virtual loop) segment start from the
  // caller — already mapped into 0…mediaDuration for full-scale archive.
  let offsetSec = (targetMs - segment.start.getTime()) / 1000;
  if (offsetSec < 0 || offsetSec > mediaDuration + 0.35) {
    offsetSec = (targetMs - loadedStartMs) / 1000;
  }

  const segmentSpanSec = (segment.end.getTime() - segment.start.getTime()) / 1000;
  if (offsetSec < 0 || offsetSec > mediaDuration + 0.35) {
    // Full-timescale mock archive: wrap the short sample instead of force-reload.
    if (segmentSpanSec > mediaDuration + 1) {
      offsetSec = ((offsetSec % mediaDuration) + mediaDuration) % mediaDuration;
    } else {
      return false;
    }
  }

  try {
    video.loop = true;
    video.currentTime = offsetSec;
  } catch {
    return false;
  }
  void video.play()?.catch(() => undefined);
  return true;
}
